import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Pencil, Trash2, Archive, ArchiveRestore, ChevronDown, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, apiReferences, getList } from "@/lib/api";
import Modal, { inputCls, btnPrimary, btnSecondary, Field } from "@/components/Modal";
import AllergenChips from "@/components/AllergenChips";
import DeleteBlockedModal from "@/components/DeleteBlockedModal";
import Pager from "@/components/Pager";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { ALLERGENS, INGREDIENT_TYPES, NUTRIENT_FIELDS, PAGE_SIZE, inr, label, fmtDate } from "@/lib/constants";

const EMPTY_NUTRITION = Object.fromEntries(NUTRIENT_FIELDS.map(([k]) => [k, 0]));

const EMPTY = {
  name: "", type: "cocoa_mass", supplier_name: "", cost_per_kg: "", allergen_flags: [],
  cocoa_origin: "", notes: "", cost_history_note: "", nutrition: EMPTY_NUTRITION,
  nutrition_source: "user_entered",
};

function PriceHistory({ ingredientId }) {
  const [open, setOpen] = useState(false);
  const { data: rows = [] } = useQuery({
    queryKey: ["price-history", ingredientId],
    queryFn: async () => (await api.get(`/ingredients/${ingredientId}/price-history`)).data,
    enabled: open,
  });

  return (
    <div className="border border-[#E8DFD1] rounded-lg overflow-hidden" data-testid="price-history-section">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[#FAF6F0] text-left"
        data-testid="toggle-price-history"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[#2A1810]">Price history</span>
        <ChevronDown size={15} className={`text-[#8C7A6B] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="divide-y divide-[#F4EFEA]" data-testid="price-history-list">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`price-row-${r.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-[#2A1810]">
                  {r.is_baseline ? inr(r.new_cost_per_kg) : `${inr(r.old_cost_per_kg)} → ${inr(r.new_cost_per_kg)}`}
                  {r.change_percent != null && (
                    <span className={`ml-2 text-xs font-semibold ${r.change_percent > 0 ? "text-[#B91C1C]" : "text-[#15803D]"}`}>
                      {r.change_percent > 0 ? "+" : ""}{r.change_percent}%
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#8C7A6B] truncate">{r.note || "—"}</div>
              </div>
              <div className="text-xs text-[#8C7A6B] text-right shrink-0">
                <div>{fmtDate(r.changed_at)}</div>
                <div>{r.changed_by_name || "—"}</div>
              </div>
            </div>
          ))}
          {!rows.length && (
            <p className="px-4 py-3 text-sm text-[#8C7A6B]" data-testid="price-history-empty">No price changes recorded yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

function IngredientForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial
      ? { ...EMPTY, ...initial, nutrition: { ...EMPTY_NUTRITION, ...(initial.nutrition || {}) } }
      : EMPTY
  );
  const [saving, setSaving] = useState(false);
  const [showNutrition, setShowNutrition] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estimated, setEstimated] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setNutrient = (k) => (e) =>
    setForm({ ...form, nutrition: { ...form.nutrition, [k]: e.target.value } });
  const costMoved = Boolean(initial?.id) &&
    Number(initial.cost_per_kg).toFixed(2) !== (Number(form.cost_per_kg) || 0).toFixed(2);

  const estimateNutrition = async () => {
    setEstimating(true);
    try {
      const res = await api.post("/ai/ingredients/nutrition-estimate", {
        name: form.name.trim(),
        type: form.type,
      });
      setForm((f) => ({ ...f, nutrition: { ...f.nutrition, ...res.data.nutrition } }));
      setEstimated(true);
      toast.success("AI estimate filled in — review before saving");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setEstimating(false);
    }
  };

  const toggleAllergen = (a) =>
    setForm({
      ...form,
      allergen_flags: form.allergen_flags.includes(a)
        ? form.allergen_flags.filter((x) => x !== a)
        : [...form.allergen_flags, a],
    });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost_per_kg: Number(form.cost_per_kg) || 0,
        nutrition: Object.fromEntries(NUTRIENT_FIELDS.map(([k]) => [k, Number(form.nutrition[k]) || 0])),
        nutrition_source: estimated ? "ai_estimate" : (form.nutrition_source || "user_entered"),
      };
      if (initial?.id) {
        await api.put(`/ingredients/${initial.id}`, payload);
        toast.success("Ingredient updated");
      } else {
        await api.post("/ingredients", payload);
        toast.success("Ingredient created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={initial?.id ? "Edit Ingredient" : "New Ingredient"} onClose={onClose} testid="ingredient-form-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="ingredient-form">
        <Field label="Name">
          <input data-testid="ingredient-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Type">
            <select data-testid="ingredient-type-select" value={form.type} onChange={set("type")} className={inputCls}>
              {INGREDIENT_TYPES.map((t) => <option key={t} value={t}>{label(t)}</option>)}
            </select>
          </Field>
          <Field label="Cost per kg (INR)">
            <input data-testid="ingredient-cost-input" type="number" step="0.01" min={0} required value={form.cost_per_kg} onChange={set("cost_per_kg")} className={inputCls} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Supplier">
            <input data-testid="ingredient-supplier-input" value={form.supplier_name} onChange={set("supplier_name")} className={inputCls} />
          </Field>
          <Field label="Cocoa origin">
            <input data-testid="ingredient-origin-input" value={form.cocoa_origin || ""} onChange={set("cocoa_origin")} className={inputCls} placeholder="Optional" />
          </Field>
        </div>
        <Field label="Allergens">
          <div className="flex flex-wrap gap-2 pt-1" data-testid="ingredient-allergen-picker">
            {ALLERGENS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => toggleAllergen(a)}
                data-testid={`allergen-toggle-${a}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  form.allergen_flags.includes(a)
                    ? "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]"
                    : "bg-white text-[#5C4A3E] border-[#E8DFD1] hover:border-[#C5A059]"
                }`}
              >
                {a.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </Field>
        <Field label={costMoved ? "Reason for price change (required)" : "Cost history note"}>
          <input
            data-testid="ingredient-cost-note-input"
            required={costMoved}
            value={form.cost_history_note}
            onChange={set("cost_history_note")}
            className={inputCls}
            placeholder="e.g. +8% from Apr 2026 contract"
          />
          {costMoved && (
            <p className="text-xs text-[#92400E] mt-1.5" data-testid="price-change-reason-hint">
              Cost moves from {inr(initial.cost_per_kg)} to {inr(Number(form.cost_per_kg) || 0)} — a reason is logged to the price history.
            </p>
          )}
        </Field>

        {initial?.id && <PriceHistory ingredientId={initial.id} />}
        <Field label="Notes">
          <textarea data-testid="ingredient-notes-input" rows={2} value={form.notes} onChange={set("notes")} className={inputCls} />
        </Field>

        <div className="border border-[#E8DFD1] rounded-lg overflow-hidden" data-testid="nutrition-fields-section">
          <button
            type="button"
            onClick={() => setShowNutrition(!showNutrition)}
            className="w-full flex items-center justify-between px-4 py-3 bg-[#FAF6F0] text-left"
            data-testid="toggle-nutrition-fields"
          >
            <span className="text-xs font-semibold uppercase tracking-wider text-[#2A1810]">
              Nutrition per 100 g
              {Number(form.nutrition.energy_kcal) > 0 && (
                <span className="ml-2 text-[#15803D] normal-case tracking-normal font-medium">
                  · {form.nutrition.energy_kcal} kcal on file
                </span>
              )}
            </span>
            <ChevronDown size={15} className={`text-[#8C7A6B] transition-transform ${showNutrition ? "rotate-180" : ""}`} />
          </button>
          {showNutrition && (
            <div className="px-4 py-4">
              <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-[#F4EFEA]">
                <p className="text-xs text-[#8C7A6B] leading-relaxed">
                  Used to build the FSSAI nutrition panel on every formula containing this ingredient.
                </p>
                <button
                  type="button"
                  onClick={estimateNutrition}
                  disabled={estimating || !form.name.trim()}
                  className="shrink-0 bg-[#F9F3E6] hover:bg-[#F4EBD7] text-[#8A6D2F] border border-[#E5C992] px-3 py-2 rounded-lg font-semibold text-xs transition-colors flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="ai-estimate-nutrition-button"
                >
                  {estimating ? <RefreshCw size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  {estimating ? "Estimating…" : "AI estimate"}
                </button>
              </div>
              {estimated && (
                <div className="mb-4 text-xs text-[#8A6D2F] bg-[#F9F3E6] border border-[#E5C992] rounded-lg px-3 py-2.5 leading-relaxed" data-testid="ai-estimate-notice">
                  <span className="font-bold uppercase tracking-wider">Unverified AI estimate.</span>{" "}
                  Review every value against supplier data before saving — nothing is stored until you save.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {NUTRIENT_FIELDS.map(([key, labelText, max]) => (
                  <Field key={key} label={labelText}>
                    <input
                      data-testid={`nutrition-${key}-input`}
                      type="number"
                      step="0.1"
                      min={0}
                      max={max}
                      value={form.nutrition[key]}
                      onChange={setNutrient(key)}
                      className={inputCls}
                    />
                  </Field>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary} data-testid="ingredient-cancel-button">Cancel</button>
          <button type="submit" disabled={saving} className={btnPrimary} data-testid="ingredient-save-button">
            {saving ? "Saving…" : "Save ingredient"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function Ingredients() {
  const { canWrite } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ q: "", type: "", allergen: "" });
  const [includeArchived, setIncludeArchived] = useState(false);
  const [offset, setOffset] = useState(0);
  const [form, setForm] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [archiving, setArchiving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["ingredients-page", filters, includeArchived, offset],
    queryFn: () => getList("/ingredients", { ...filters, include_archived: includeArchived || undefined, limit: PAGE_SIZE, offset }),
  });

  const items = data?.items ?? [];
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["ingredients-page"] });
    queryClient.invalidateQueries({ queryKey: ["ingredients"] });
  };

  const setFilter = (k) => (e) => { setOffset(0); setFilters({ ...filters, [k]: e.target.value }); };

  const toggleArchive = async (item) => {
    try {
      await api.post(`/ingredients/${item.id}/archive`, { archived: !item.archived });
      toast.success(item.archived ? "Ingredient restored" : "Ingredient archived");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const del = async (item) => {
    const ok = await confirm({
      title: "Delete ingredient",
      message: `Delete "${item.name}"? This cannot be undone. If it is used in any formula, you'll be offered the option to archive it instead.`,
      confirmLabel: "Delete ingredient",
    });
    if (!ok) return;
    try {
      await api.delete(`/ingredients/${item.id}`);
      toast.success("Ingredient deleted");
      refresh();
    } catch (err) {
      const references = apiReferences(err);
      if (references) setBlocked({ item, references, message: apiError(err) });
      else toast.error(apiError(err));
    }
  };

  const archiveFromBlocked = async () => {
    setArchiving(true);
    try {
      await api.post(`/ingredients/${blocked.item.id}/archive`, { archived: true });
      toast.success("Ingredient archived");
      setBlocked(null);
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div data-testid="ingredients-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-4 border-b border-[#E8DFD1]">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#1C100B]">Ingredients</h1>
          <p className="text-sm text-[#8C7A6B] mt-1">Supplier library, costs and allergen declarations.</p>
        </div>
        {canWrite && (
          <button onClick={() => setForm({})} className={btnPrimary} data-testid="new-ingredient-button">
            <Plus size={16} /> New Ingredient
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-6 items-center" data-testid="ingredient-filters">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C7A6B]" />
          <input
            data-testid="ingredient-search-input"
            placeholder="Search ingredients…"
            value={filters.q}
            onChange={setFilter("q")}
            className={`${inputCls} pl-9 w-56`}
          />
        </div>
        <select data-testid="ingredient-type-filter" value={filters.type} onChange={setFilter("type")} className={`${inputCls} w-48`}>
          <option value="">All types</option>
          {INGREDIENT_TYPES.map((t) => <option key={t} value={t}>{label(t)}</option>)}
        </select>
        <select data-testid="ingredient-allergen-filter" value={filters.allergen} onChange={setFilter("allergen")} className={`${inputCls} w-48`}>
          <option value="">All allergens</option>
          {ALLERGENS.map((a) => <option key={a} value={a}>{label(a)}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs font-semibold text-[#5C4A3E] cursor-pointer" data-testid="include-archived-toggle">
          <input type="checkbox" checked={includeArchived} onChange={(e) => { setOffset(0); setIncludeArchived(e.target.checked); }} className="accent-[#C5A059]" data-testid="include-archived-checkbox" />
          Include archived
        </label>
      </div>

      <div className="w-full overflow-x-auto rounded-xl border border-[#E8DFD1] bg-white shadow-sm" data-testid="ingredients-table">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#FAF6F0] border-b border-[#E8DFD1] text-[#2A1810] text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3.5 font-semibold">Name</th>
              <th className="px-4 py-3.5 font-semibold">Type</th>
              <th className="px-4 py-3.5 font-semibold">Supplier</th>
              <th className="px-4 py-3.5 font-semibold text-right">Cost / kg</th>
              <th className="px-4 py-3.5 font-semibold">Allergens</th>
              <th className="px-4 py-3.5 font-semibold">Cost note</th>
              {canWrite && <th className="px-4 py-3.5" />}
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#8C7A6B]" data-testid="ingredients-loading">Loading…</td></tr>}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#8C7A6B]" data-testid="ingredients-empty">No ingredients match these filters.</td></tr>
            )}
            {items.map((i) => (
              <tr key={i.id} className="border-b border-[#F4EFEA] last:border-0 hover:bg-[#FDFBF7] transition-colors" data-testid={`ingredient-row-${i.id}`}>
                <td className="px-4 py-3.5 font-medium text-[#2A1810]">
                  {i.name}
                  {i.archived && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-[#F3F4F6] text-[#374151] border border-[#E5E7EB] px-1.5 py-0.5 rounded" data-testid={`archived-badge-${i.id}`}>Archived</span>}
                </td>
                <td className="px-4 py-3.5 text-[#5C4A3E]">{label(i.type)}</td>
                <td className="px-4 py-3.5 text-[#5C4A3E]">{i.supplier_name || "—"}</td>
                <td className="px-4 py-3.5 text-right font-medium text-[#2A1810]">{inr(i.cost_per_kg)}</td>
                <td className="px-4 py-3.5"><AllergenChips allergens={i.allergen_flags} testid={`ingredient-allergens-${i.id}`} /></td>
                <td className="px-4 py-3.5 text-[#8C7A6B] max-w-[200px]"><span className="line-clamp-2">{i.cost_history_note || "—"}</span></td>
                {canWrite && (
                  <td className="px-4 py-3.5">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => setForm(i)} className="p-1.5 rounded hover:bg-[#FAF6F0] text-[#5C4A3E]" data-testid={`edit-ingredient-${i.id}`}><Pencil size={14} /></button>
                      <button onClick={() => toggleArchive(i)} className="p-1.5 rounded hover:bg-[#F9F3E6] text-[#8A6D2F]" data-testid={`archive-ingredient-${i.id}`}>
                        {i.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                      </button>
                      <button onClick={() => del(i)} className="p-1.5 rounded hover:bg-[#FEF2F2] text-[#B91C1C]" data-testid={`delete-ingredient-${i.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <Pager offset={offset} limit={PAGE_SIZE} total={data?.total ?? 0} onChange={setOffset} testid="ingredients-pager" />
      </div>

      {form && <IngredientForm initial={form.id ? form : null} onClose={() => setForm(null)} onSaved={refresh} />}
      {blocked && (
        <DeleteBlockedModal
          message={blocked.message}
          references={blocked.references}
          onClose={() => setBlocked(null)}
          onArchive={archiveFromBlocked}
          archiving={archiving}
        />
      )}
    </div>
  );
}
